import Sections from 'components/sections';
import Breadcrumbs from 'components/shared/breadcrumbs';
import { useUI } from 'hooks';
import { getResourceText } from 'lib/resources';
import { useRouter } from 'next/router';
import { trackEvents, trackInWindow } from 'hooks/tracking/track';
import useSWRInfinite, { unstable_serialize as serialize } from 'swr/infinite';
import collectionQuery from 'lib/shopify/queries/collection';
import retryFetch from 'lib/shopify/retryFetch';
import { parseMetafields } from 'lib/shopify/fetchCollectionProducts';
import { useRef, useState, useEffect } from 'react';
import useInViewCallback from 'hooks/utils/useInViewCallback';
import { Spinner } from 'components/shared/icons/ui';
import Link from 'next/link';
import HighstreetAppMetaUrl from 'components/highstreet-app-meta-url';
import toItem from 'hooks/tracking/to-item';
import stripShopifyGid from 'lib/utils/stripShopifyGid';
import renderRichtext from 'lib/render-richtext';
import { capitalize } from 'lodash';
import Navigation from './navigation';
import ProductGrid from './productGrid';
import Toolbar from './toolbar';
import useCollectionQuery from './useCollectionQuery';
import shopifyFilterIdToKey from './shopifyFilterIdToKey';
import FilterSidebar, { tKey } from './filterSidebar';
import ProductListingStructuredData from './productListingStructuredData';

const availableSorts = {
  recommended: {
    key: 'COLLECTION_DEFAULT',
  },
  priceLowToHigh: {
    key: 'PRICE',
  },
  priceHighToLow: {
    key: 'PRICE',
    reverse: true,
  },
  newArrivals: {
    key: 'MANUAL',
  },
};

// Filters (configure in Shopify)
const filterTypes = [
  // 'availability',
  'gender',
  'genericProductType',
  'genericFit',
  'specialfeaturesv2',
  'size',
  'waist',
  'length',
  'genericColorName',
  'style',
  'price',
];

const defaultSort = Object.keys(availableSorts)[0];

export const collectionPageFetcher =
  store =>
  async (handle, sort = 'recommended', filtersInput, after) => {
    const perPage = 20;

    const sortKey = availableSorts[sort]?.key || availableSorts[defaultSort].key;
    const reverse = availableSorts[sort]?.reverse || false;

    const {
      data: { collection },
    } = await retryFetch(store, {
      query: collectionQuery,
      variables: {
        handle,
        sortKey,
        reverse,
        filters: [
          ...(filtersInput && filtersInput.length ? [...filtersInput, { available: true }] : []),
        ],
        first: perPage,
        ...(after ? { after } : {}),
      },
    });

    if (!collection) return null;

    const {
      title: collectionTitle,
      seo: { title: seoTitle },
      products,
      products: { filters: rawFilters },
    } = collection;

    const title = seoTitle || collectionTitle;

    const filters = rawFilters
      .map(filter => ({
        key: shopifyFilterIdToKey(filter.id),
        ...filter,
      }))
      // excluding _ options (used by some filters for empty)
      .map(filter => ({
        ...filter,
        values: filter.values.filter(({ label }) => label !== '_'),
      }))
      // exclude options/filters without results
      .map(filter => ({
        ...filter,
        values: ['f.price', 'f.waist', 'f.size', 'f.length'].includes(filter.key)
          ? filter.values
          : filter.values.filter(({ count }) => count > 0),
      }))
      // exclude jean filters on non-jean collections
      .filter(({ key }) =>
        !title.toLowerCase().includes('jeans')
          ? !['f.style', 'f.specialfeaturesv2'].includes(key)
          : true
      )
      .filter(({ key, values: { length: optionCount } }) => optionCount > 1 || key === 'f.price');

    return { ...collection, products: { ...products, filters } };
  };

const ProductsListing = props => {
  const {
    page: {
      slug,
      title,
      gridLayout,
      desktopGridLayout,
      breadcrumbsCollection: { items: breadcrumbs },
      gridBannersCollection: { items: banners },
      sectionsCollection: sections,
      shopifyHandle: handle,
    },
    colors,
    page,
    microcopies,
    currency,
    store,
    initialCollection,
  } = props;

  const router = useRouter();
  const nextPageRef = useRef();
  const { shiftContent, shiftContentDesktop } = useUI();

  const {
    products: { filters: initialFilters = {} },
  } = initialCollection;

  const navigationLinks = (
    page.navigation?.resourcesCollection?.items?.filter(Boolean) || []
  ).filter(({ link }) => link);
  const t = getResourceText(microcopies);

  const [previousCollections, setPreviousCollections] = useState([initialCollection]);
  const {
    sort,
    getSortUrl,
    getResetUrl,
    getFilterValues,
    getShopifyFilterInputs,
    isFilterAvailable,
    getFilterUrl,
  } = useCollectionQuery();

  const {
    data: collections,
    error,
    size,
    setSize,
    isValidating: isLoading,
  } = useSWRInfinite(
    (_, previousCollection) => {
      if (!previousCollection) return [handle, sort, getShopifyFilterInputs(initialFilters)];

      const {
        products: {
          filters,
          pageInfo: { hasNextPage, endCursor },
        },
      } = previousCollection;

      if (!hasNextPage) return null;

      return [handle, sort, getShopifyFilterInputs(filters), endCursor];
    },
    collectionPageFetcher(store),
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateOnReconnect: false,
      revalidateFirstPage: false,
      fallbackData: previousCollections,
      onSuccess: setPreviousCollections,
      fallback: {
        [serialize(() => [handle])]: initialCollection,
      },
    }
  );

  if (error) console.error(error);

  const products = collections
    .flatMap(({ products: { edges } }) => edges)
    .map(({ node }) => node)
    .map(parseMetafields);

  const {
    filters,
    pageInfo: { hasNextPage },
  } = collections.length
    ? collections[collections.length - 1].products
    : { filters: [], pageInfo: { hasNextPage: false } };

  useInViewCallback(
    nextPageRef,
    () => {
      if (!isLoading && hasNextPage) setSize(size + 1);
    },
    '0px 0px 1400px 0px'
  );

  const isLoadingNextPage = collections.length < size && isLoading;
  const isLoadingSortOrFilters = isLoading && !isLoadingNextPage;

  useEffect(() => {
    trackInWindow({
      event: trackEvents.collectionPageView,
      page_path: router?.asPath,
      page_title: document.title,
      collection_name: title || slug,
      collection_id: initialCollection?.id ? stripShopifyGid(initialCollection.id) : null,
      ecommerce: {
        currency,
        items: products.map(p => toItem(p)),
      },
    });
  }, []);

  const filterValues = getFilterValues(filters);
  const resetUrl = getResetUrl(filters);

  const colorHexCodes = Object.fromEntries(
    colors.filter(c => c.length === 2 && c[0] !== 'Color name')
  );

  const activeFilterValues = (filters || [])
    .filter(({ key }) => filterValues[key])
    .map(({ key }) =>
      filterValues[key].map(value => ({
        link: getFilterUrl(key, value),
        label: [
          ['f.size', 'f.waist', 'f.length'].includes(key)
            ? t(`filter - ${tKey(key)}`, tKey(key))
            : null,
          t(
            `filter options - ${tKey(key)} - ${value}`,
            tKey(key) === 'color' ? value.split(' ').map(capitalize).join(' ') : value
          ),
        ]
          .filter(Boolean)
          .join(' '),
      }))
    )
    .filter(Boolean)
    .flat();

  return (
    <>
      <ProductListingStructuredData page={page} products={products} />
      {page?.appCategoryId && <HighstreetAppMetaUrl url={`categories/${page.appCategoryId}`} />}
      <FilterSidebar
        t={t}
        isLoading={isLoadingSortOrFilters}
        colorHexCodes={colorHexCodes}
        filters={filterTypes // Match filter types with Shopify filters
          .map(f => filters.find(({ key }) => key === `f.${f}`))
          .filter(Boolean)
          .filter(f => f.key !== 'f.price')}
        getFilterUrl={getFilterUrl}
        isAvailable={(key, label) => isFilterAvailable(filters, key, label)}
        filterValues={filterValues}
        resetUrl={resetUrl}
      />
      <main>
        <Breadcrumbs
          className="hidden md:flex"
          items={breadcrumbs}
          homeLabel={t('Home')}
          currentLabel={title}
        />
        {navigationLinks.length > 0 && <Navigation links={navigationLinks} activeSlug={slug} />}
        <div className="space-y-4 px-4 py-[1.5625rem] pb-[calc(1.5625rem-1rem)] text-center lg:space-y-10 lg:py-[3.25rem] lg:pb-[calc(3.25rem-1rem)]">
          {(page.pageTitle || page.title) && (
            <h1 className="font-nimbus-sans-extd-d text-[30px] font-bold uppercase leading-[0.9]">
              {page.pageTitle || page.title}
            </h1>
          )}
          {page.description && (
            <div className="mx-auto max-w-[90%] space-y-2 text-[14px] lg:max-w-[37.5rem] lg:text-[15px]">
              {renderRichtext(page.description)}
            </div>
          )}
        </div>
        <div className="relative pb-12">
          <Toolbar
            activeFiltersCount={activeFilterValues.length}
            isLoading={isLoadingSortOrFilters}
            style={{ '--top': shiftContent, '--topDesktop': shiftContentDesktop }}
            className="sticky top-[var(--top)] z-10 md:top-[var(--topDesktop)]"
            t={t}
            sort={sort}
            sortUrls={Object.entries(availableSorts).reduce(
              (acc, [k]) => ({ ...acc, [k]: getSortUrl(k) }),
              {}
            )}
          />
          {/* Active filters */}
          {activeFilterValues.length > 0 && (
            <div className="mb-6 flex gap-2 overflow-x-auto px-4">
              {activeFilterValues.map(({ link, label }) => (
                <Link key={label} shallow passHref href={link}>
                  <a
                    href="_"
                    className="text-body-1 flex h-8 w-fit shrink-0 items-center gap-1 border border-blue/10 px-3 pr-1 leading-none lg:hover:border-blue"
                  >
                    <span className="mt-[2px]">{label}</span>
                    <svg
                      className="w-4"
                      preserveAspectRatio="xMidYMid meet"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.99966 8.48754L10.5121 11.0007L11 10.5135L8.48685 8.00034L11 5.48788L10.5128 5L7.99966 7.51315L5.48719 5L5 5.48788L7.51246 8.00034L5 10.5128L5.48719 11.0007L7.99966 8.48754Z"
                        fill="#041C2C"
                      />
                    </svg>
                  </a>
                </Link>
              ))}
            </div>
          )}
          {products.length > 0 ? (
            <ProductGrid
              layout={gridLayout}
              desktopLayout={desktopGridLayout}
              banners={banners.filter(Boolean)}
              products={products}
              t={t}
            />
          ) : (
            <section className="px-4 py-24 text-center md:pb-48">
              <p className="mb-5 font-nimbus-sans-extd-d text-xl font-bold leading-[0.9]">
                {t('empty - No products found')}
              </p>
              {Object.keys(filterValues).length > 0 && (
                <Link href={resetUrl} shallow passHref>
                  <a href="-" className="button">
                    {t('empty - Clear filters')}
                  </a>
                </Link>
              )}
            </section>
          )}
          {hasNextPage && (
            <div ref={nextPageRef} className="flex h-24 w-full items-center justify-center">
              {isLoadingNextPage && <Spinner className="h-16 w-16" />}
            </div>
          )}
        </div>
        {sections?.items.length > 0 && (
          <div className="pt-[15px] md:pt-[30px]">
            <Sections data={sections} />
          </div>
        )}
      </main>
    </>
  );
};

export default ProductsListing;
